import React, { useState, useEffect } from 'react';
import './Interaction.css';

function Interaction({ selectedElement, elements, updateElementInteractions }) {
  const [selectedClickAction, setSelectedClickAction] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [targetElement, setTargetElement] = useState('');
  const [toggleClass, setToggleClass] = useState('');
  const [scrollBehavior, setScrollBehavior] = useState('smooth');
  const [openInNewTab, setOpenInNewTab] = useState(false);
  const [appliedEvents, setAppliedEvents] = useState([]);
  const [openSection, setOpenSection] = useState(null);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [clickSequence, setClickSequence] = useState('first');
  const [displayProperty, setDisplayProperty] = useState('block');
  const [selectedAnimation, setSelectedAnimation] = useState('');
  const [animationDuration, setAnimationDuration] = useState('1s');
  const [animationDelay, setAnimationDelay] = useState('0s');
  const [animationIterations, setAnimationIterations] = useState('1');
  const [isPreviewingAnimation, setIsPreviewingAnimation] = useState(false);
  const [previewingEventId, setPreviewingEventId] = useState(null);
  const [selectedTrigger, setSelectedTrigger] = useState('onView');
  
  // Hover event states
  const [selectedHoverAction, setSelectedHoverAction] = useState('');
  const [hoverTargetElement, setHoverTargetElement] = useState('');
  const [hoverClass, setHoverClass] = useState('');
  const [scaleValue, setScaleValue] = useState('1.1');
  const [opacityValue, setOpacityValue] = useState('0.8');
  const [colorValue, setColorValue] = useState('#3498db');
  const [hoverTransitionDuration, setHoverTransitionDuration] = useState('0.3s');
  const [hoverDirection, setHoverDirection] = useState('in'); // 'in' for mouseenter, 'out' for mouseleave

  // Tab click event states
  const [tabsParentId, setTabsParentId] = useState('');
  const [tabButtonClass, setTabButtonClass] = useState('active');
  const [tabsContentParentId, setTabsContentParentId] = useState('');
  const [tabContentClass, setTabContentClass] = useState('active');

  useEffect(() => {
    console.log("selectedElement in Interaction.js", selectedElement);
  }, [selectedElement]);

  useEffect(() => {
    if (selectedElement && selectedElement.interactions?.events) {
      setAppliedEvents(selectedElement.interactions.events);
    } else {
      setAppliedEvents([]);
    }
  }, [selectedElement]);

  useEffect(() => {
    setSelectedClickAction('');
    setLinkUrl('');
    setTargetElement('');
    setToggleClass('');
    setScrollBehavior('smooth');
    setOpenInNewTab(false);
    // Reset hover states
    setSelectedHoverAction('');
    setHoverTargetElement('');
    setHoverClass('');
    setScaleValue('1.1');
    setOpacityValue('0.8');
    setColorValue('#3498db');
    setHoverTransitionDuration('0.3s');
    setHoverDirection('in');
    // Reset tab states
    setTabsParentId('');
    setTabButtonClass('active');
    setTabsContentParentId('');
    setTabContentClass('active');
  }, [selectedElement?.id]);

  const handleClickActionChange = (action, sequence) => {
    setClickSequence(sequence);
    setSelectedClickAction(action);
    setLinkUrl('');
    setTargetElement('');
    setToggleClass('');
    setScrollBehavior('smooth');
    setOpenInNewTab(false);
  };

  const handleHoverActionChange = (action) => {
    setSelectedHoverAction(action);
    setHoverTargetElement('');
    setHoverClass('');
    setScaleValue('1.1');
    setOpacityValue('0.8');
    setColorValue('#3498db');
    setDisplayProperty('block');
  };

  const getElementLabel = (element) => {
    if (element.className) {
      return `${element.type} (${element.className})`;
    }
    return `${element.type} ${element.id}`;
  };

  const handleApplyEvent = () => {
    if (!selectedClickAction) return;

    console.log("Applying event to element:", selectedElement);

    const newEvent = {
      id: Date.now(),
      type: 'click',
      sequence: clickSequence,
      action: selectedClickAction,
      value: {}
    };

    switch (selectedClickAction) {
      case 'link':
        if (!linkUrl.trim()) return;
        newEvent.value = { url: linkUrl, newTab: openInNewTab };
        newEvent.label = `${clickSequence === 'first' ? '1st' : '2nd'} Click: Open Link: ${linkUrl}${openInNewTab ? ' (new tab)' : ''}`;
        break;

      case 'toggle':
        if (!targetElement) return;
        newEvent.value = { 
          targetId: targetElement,
          display: displayProperty
        };
        const targetEl = elements.find(el => el.id === parseInt(targetElement));
        const targetLabel = targetEl?.className || `${targetEl?.type} ${targetElement}`;
        newEvent.label = `${clickSequence === 'first' ? '1st' : '2nd'} Click: Toggle Display (${displayProperty}): ${targetLabel}`;
        break;

      case 'toggleClass':
        if (!targetElement || !toggleClass.trim()) return;
        newEvent.value = { targetId: targetElement, className: toggleClass };
        const targetElement2 = elements.find(el => el.id === parseInt(targetElement));
        newEvent.label = `${clickSequence === 'first' ? '1st' : '2nd'} Click: Toggle Class: ${toggleClass} on ${targetElement2?.type || 'Element'} ${targetElement}`;
        break;

      case 'scroll':
        if (!targetElement) return;
        newEvent.value = { targetId: targetElement, behavior: scrollBehavior };
        const targetElement3 = elements.find(el => el.id === parseInt(targetElement));
        newEvent.label = `${clickSequence === 'first' ? '1st' : '2nd'} Click: Scroll To: ${targetElement3?.type || 'Element'} ${targetElement} (${scrollBehavior})`;
        break;

      default:
        return;
    }

    const filteredEvents = appliedEvents.filter(event => 
      !(event.type === 'click' && event.sequence === clickSequence)
    );

    const updatedEvents = [...filteredEvents, newEvent];
    console.log("Updated events:", updatedEvents);
    setAppliedEvents(updatedEvents);
    updateElementInteractions(selectedElement.id, 'events', updatedEvents);

    // Reset form
    setSelectedClickAction('');
    setLinkUrl('');
    setTargetElement('');
    setToggleClass('');
    setScrollBehavior('smooth');
    setOpenInNewTab(false);
  };

  const handleApplyHoverEvent = () => {
    if (!selectedHoverAction) return;

    const newEvent = {
      id: Date.now(),
      type: 'hover',
      direction: hoverDirection,
      action: selectedHoverAction,
      value: {
        transitionDuration: hoverTransitionDuration
      }
    };

    switch (selectedHoverAction) {
      case 'scale':
        newEvent.value = { 
          ...newEvent.value,
          scale: scaleValue
        };
        newEvent.label = `${hoverDirection === 'in' ? 'Hover In' : 'Hover Out'}: Scale ${scaleValue}`;
        break;

      case 'opacity':
        newEvent.value = { 
          ...newEvent.value,
          opacity: opacityValue
        };
        newEvent.label = `${hoverDirection === 'in' ? 'Hover In' : 'Hover Out'}: Opacity ${opacityValue}`;
        break;

      case 'color':
        newEvent.value = { 
          ...newEvent.value,
          color: colorValue
        };
        newEvent.label = `${hoverDirection === 'in' ? 'Hover In' : 'Hover Out'}: Change Color`;
        break;

      case 'toggle':
        if (!hoverTargetElement) return;
        newEvent.value = { 
          ...newEvent.value,
          targetId: hoverTargetElement,
          display: displayProperty
        };
        const targetEl = elements.find(el => el.id === parseInt(hoverTargetElement));
        const targetLabel = targetEl?.className || `${targetEl?.type} ${hoverTargetElement}`;
        newEvent.label = `${hoverDirection === 'in' ? 'Hover In' : 'Hover Out'}: Toggle Display (${displayProperty}): ${targetLabel}`;
        break;

      case 'toggleClass':
        if (!hoverClass.trim()) return;
        newEvent.value = { 
          ...newEvent.value,
          className: hoverClass 
        };
        newEvent.label = `${hoverDirection === 'in' ? 'Hover In' : 'Hover Out'}: Toggle Class ${hoverClass}`;
        break;

      default:
        return;
    }

    // Filter out existing hover events with the same direction
    const filteredEvents = appliedEvents.filter(event => 
      !(event.type === 'hover' && event.direction === hoverDirection && event.action === selectedHoverAction)
    );

    const updatedEvents = [...filteredEvents, newEvent];
    setAppliedEvents(updatedEvents);
    updateElementInteractions(selectedElement.id, 'events', updatedEvents);

    // Reset form (optional)
    setSelectedHoverAction('');
    setHoverClass('');
    setScaleValue('1.1');
    setOpacityValue('0.8');
    setColorValue('#3498db');
    setHoverTargetElement('');
  };

  const removeEvent = (eventId) => {
    const updatedEvents = appliedEvents.filter(event => event.id !== eventId);
    setAppliedEvents(updatedEvents);
    updateElementInteractions(selectedElement.id, 'events', updatedEvents);
  };

  const renderActionSettings = () => {
    switch (selectedClickAction) {
      case 'link':
        return (
          <>
            <div className="property-group">
              <label>URL</label>
              <input 
                type="text" 
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
            <div className="property-group">
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={openInNewTab}
                  onChange={(e) => setOpenInNewTab(e.target.checked)}
                />
                Open in new tab
              </label>
            </div>
          </>
        );

      case 'toggle':
        return (
          <>
            <div className="property-group">
              <label>Target Element</label>
              <select 
                value={targetElement}
                onChange={(e) => setTargetElement(e.target.value)}
              >
                <option value="">Select element</option>
                {elements
                  .filter(element => element.className)
                  .map(element => (
                    <option key={element.id} value={element.id}>
                      {getElementLabel(element)}
                    </option>
                  ))
                }
              </select>
            </div>
            <div className="property-group">
              <label>Display Property</label>
              <select
                value={displayProperty}
                onChange={(e) => setDisplayProperty(e.target.value)}
                className="interaction-select"
              >
                <option value="block">Block</option>
                <option value="flex">Flex</option>
                <option value="grid">Grid</option>
                <option value="none">None</option>
              </select>
            </div>
          </>
        );

      case 'toggleClass':
        return (
          <>
            <div className="property-group">
              <label>Target Element</label>
              <select 
                value={targetElement}
                onChange={(e) => setTargetElement(e.target.value)}
              >
                <option value="">Select element</option>
                {elements
                  .filter(element => element.className)
                  .map(element => (
                    <option key={element.id} value={element.id}>
                      {getElementLabel(element)}
                    </option>
                  ))
                }
              </select>
            </div>
            <div className="property-group">
              <label>Class Name</label>
              <input 
                type="text"
                value={toggleClass}
                onChange={(e) => setToggleClass(e.target.value)}
                placeholder="active"
              />
            </div>
          </>
        );

      case 'scroll':
        return (
          <>
            <div className="property-group">
              <label>Scroll To Element</label>
              <select 
                value={targetElement}
                onChange={(e) => setTargetElement(e.target.value)}
              >
                <option value="">Select element</option>
                {elements
                  .filter(element => element.className)
                  .map(element => (
                    <option key={element.id} value={element.id}>
                      {getElementLabel(element)}
                    </option>
                  ))
                }
              </select>
            </div>
            <div className="property-group">
              <label>Scroll Behavior</label>
              <select
                value={scrollBehavior}
                onChange={(e) => setScrollBehavior(e.target.value)}
              >
                <option value="smooth">Smooth</option>
                <option value="auto">Instant</option>
              </select>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const renderHoverActionSettings = () => {
    switch (selectedHoverAction) {
      case 'scale':
        return (
          <>
            <div className="property-group">
              <label>Scale Value</label>
              <input 
                type="number" 
                step="0.05"
                min="0.1"
                max="3"
                value={scaleValue}
                onChange={(e) => setScaleValue(e.target.value)}
              />
            </div>
            <div className="property-group">
              <label>Transition Duration</label>
              <input 
                type="text"
                value={hoverTransitionDuration}
                onChange={(e) => setHoverTransitionDuration(e.target.value)}
                placeholder="0.3s"
              />
            </div>
          </>
        );

      case 'opacity':
        return (
          <>
            <div className="property-group">
              <label>Opacity Value</label>
              <input 
                type="number" 
                step="0.05"
                min="0"
                max="1"
                value={opacityValue}
                onChange={(e) => setOpacityValue(e.target.value)}
              />
            </div>
            <div className="property-group">
              <label>Transition Duration</label>
              <input 
                type="text"
                value={hoverTransitionDuration}
                onChange={(e) => setHoverTransitionDuration(e.target.value)}
                placeholder="0.3s"
              />
            </div>
          </>
        );

      case 'color':
        return (
          <>
            <div className="property-group">
              <label>Color</label>
              <input 
                type="color"
                value={colorValue}
                onChange={(e) => setColorValue(e.target.value)}
              />
            </div>
            <div className="property-group">
              <label>Transition Duration</label>
              <input 
                type="text"
                value={hoverTransitionDuration}
                onChange={(e) => setHoverTransitionDuration(e.target.value)}
                placeholder="0.3s"
              />
            </div>
          </>
        );

      case 'toggle':
        return (
          <>
            <div className="property-group">
              <label>Target Element</label>
              <select 
                value={hoverTargetElement}
                onChange={(e) => setHoverTargetElement(e.target.value)}
              >
                <option value="">Select element</option>
                {elements
                  .filter(element => element.className)
                  .map(element => (
                    <option key={element.id} value={element.id}>
                      {getElementLabel(element)}
                    </option>
                  ))
                }
              </select>
            </div>
            <div className="property-group">
              <label>Display Property</label>
              <select
                value={displayProperty}
                onChange={(e) => setDisplayProperty(e.target.value)}
                className="interaction-select"
              >
                <option value="block">Block</option>
                <option value="flex">Flex</option>
                <option value="grid">Grid</option>
                <option value="none">None</option>
              </select>
            </div>
          </>
        );

      case 'toggleClass':
        return (
          <>
            <div className="property-group">
              <label>Class Name</label>
              <input 
                type="text"
                value={hoverClass}
                onChange={(e) => setHoverClass(e.target.value)}
                placeholder="hover-active"
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const toggleEventDetails = (eventId) => {
    setExpandedEventId(expandedEventId === eventId ? null : eventId);
  };

  const updateEvent = (eventId, updatedValues) => {
    const updatedEvents = appliedEvents.map(event => {
      if (event.id === eventId) {
        const updatedEvent = {
          ...event,
          value: {
            ...event.value,
            ...updatedValues
          }
        };

        // Update the label based on the event type
        const targetEl = elements.find(el => el.id === parseInt(updatedValues.targetId || event.value.targetId));
        const sequencePrefix = event.sequence === 'first' ? '1st' : '2nd';

        switch (event.action) {
          case 'link':
            updatedEvent.label = `${sequencePrefix} Click: Open Link: ${updatedValues.url || event.value.url}${updatedValues.newTab || event.value.newTab ? ' (new tab)' : ''}`;
            break;
          case 'toggle':
            const display = updatedValues.display || event.value.display || 'block';
            const targetLabel = targetEl?.className || `${targetEl?.type} ${updatedValues.targetId || event.value.targetId}`;
            updatedEvent.label = `${sequencePrefix} Click: Toggle Display (${display}): ${targetLabel}`;
            break;
          case 'toggleClass':
            updatedEvent.label = `${sequencePrefix} Click: Toggle Class: ${updatedValues.className || event.value.className} on ${targetEl?.type || 'Element'} ${updatedValues.targetId || event.value.targetId}`;
            break;
          case 'scroll':
            updatedEvent.label = `${sequencePrefix} Click: Scroll To: ${targetEl?.type || 'Element'} ${updatedValues.targetId || event.value.targetId} (${updatedValues.behavior || event.value.behavior})`;
            break;
          default:
            updatedEvent.label = `${sequencePrefix} Click: ${event.action}`;
        }

        return updatedEvent;
      }
      return event;
    });

    setAppliedEvents(updatedEvents);
    updateElementInteractions(selectedElement.id, 'events', updatedEvents);
  };

  const getEventSummaryLabel = (event) => {
    if (event.type === 'animation') {
      const animationName = predefinedAnimations[event.value.animation]?.name || event.value.animation;
      const triggerName = animationTriggers[event.trigger]?.name || 'On Trigger';
      return `${triggerName}: ${animationName}`;
    }
    
    if (event.type === 'hover') {
      const directionLabel = event.direction === 'in' ? 'Hover In: ' : 'Hover Out: ';
      
      switch (event.action) {
        case 'scale':
          return directionLabel + `Scale (${event.value.scale})`;
        case 'opacity':
          return directionLabel + `Opacity (${event.value.opacity})`;
        case 'color':
          return directionLabel + 'Change Color';
        case 'toggle':
          return directionLabel + `Toggle Display (${event.value.display})`;
        case 'toggleClass':
          return directionLabel + `Toggle Class (${event.value.className})`;
        default:
          return directionLabel + event.action;
      }
    }

    const sequenceLabel = event.sequence === 'first' ? '1st Click: ' : '2nd Click: ';
    
    switch (event.action) {
      case 'link':
        return sequenceLabel + 'Open Link';
      case 'toggle':
        return sequenceLabel + 'Toggle Display';
      case 'toggleClass':
        return sequenceLabel + 'Toggle Class';
      case 'scroll':
        return sequenceLabel + 'Scroll To';
      default:
        return sequenceLabel + event.action;
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'click':
        return '🖱️';
      case 'hover':
        return '✋';
      case 'scroll':
        return '📜';
      default:
        return '⚡';
    }
  };

  const renderClickEventsSection = () => (
    <div className="event-section">
      <div 
        className={`event-section-header ${openSection === 'click' ? 'active' : ''}`}
        onClick={() => toggleSection('click')}
      >
        <div className="event-section-title">
          <span className="event-section-icon">🖱️</span>
          <span>Click Events</span>
        </div>
        <span className={`section-arrow ${openSection === 'click' ? 'open' : ''}`}>▼</span>
      </div>
      {openSection === 'click' && (
        <div className="event-section-content">
          <div className="click-sequence">
            <h4>First Click</h4>
            <div className="property-group">
              <select 
                className="interaction-select"
                value={clickSequence === 'first' ? selectedClickAction : ''}
                onChange={(e) => handleClickActionChange(e.target.value, 'first')}
              >
                <option value="">Select action</option>
                <option value="link">Open Link</option>
                <option value="toggle">Show/Hide Element</option>
                <option value="toggleClass">Toggle Class</option>
                <option value="scroll">Scroll To Element</option>
              </select>
            </div>
            {clickSequence === 'first' && renderActionSettings()}
          </div>

          <div className="click-sequence">
            <h4>Second Click</h4>
            <div className="property-group">
              <select 
                className="interaction-select"
                value={clickSequence === 'second' ? selectedClickAction : ''}
                onChange={(e) => handleClickActionChange(e.target.value, 'second')}
              >
                <option value="">Select action</option>
                <option value="link">Open Link</option>
                <option value="toggle">Show/Hide Element</option>
                <option value="toggleClass">Toggle Class</option>
                <option value="scroll">Scroll To Element</option>
              </select>
            </div>
            {clickSequence === 'second' && renderActionSettings()}
          </div>

          {selectedClickAction && (
            <button 
              className="apply-event-btn"
              onClick={handleApplyEvent}
            >
              Apply {clickSequence === 'first' ? 'First' : 'Second'} Click Event
            </button>
          )}
        </div>
      )}
    </div>
  );

  const predefinedAnimations = {
    'fade-in': {
      name: 'Fade In',
      css: 'fade-in',
      preview: {
        opacity: [0, 1],
      }
    },
    'slide-up': {
      name: 'Slide Up',
      css: 'slide-up',
      preview: {
        transform: ['translateY(50px)', 'translateY(0)'],
        opacity: [0, 1],
      }
    },
    'slide-down': {
      name: 'Slide Down',
      css: 'slide-down',
      preview: {
        transform: ['translateY(-50px)', 'translateY(0)'],
        opacity: [0, 1],
      }
    },
    'bounce': {
      name: 'Bounce',
      css: 'bounce',
      preview: {
        transform: ['scale(1)', 'scale(1.1)', 'scale(1)'],
      }
    },
    'rotate': {
      name: 'Rotate',
      css: 'rotate',
      preview: {
        transform: ['rotate(0deg)', 'rotate(360deg)'],
      }
    },
    'shake': {
      name: 'Shake',
      css: 'shake',
      preview: {
        transform: ['translateX(0)', 'translateX(-10px)', 'translateX(10px)', 'translateX(0)'],
      }
    }
  };

  const animationTriggers = {
    'onView': {
      name: 'On Scroll Into View',
      icon: '👀'
    },
    'onClick': {
      name: 'On Click',
      icon: '🖱️'
    },
    'onHover': {
      name: 'On Hover',
      icon: '✋'
    },
    'onLoad': {
      name: 'On Page Load',
      icon: '🔄'
    },
    'onScroll': {
      name: 'While Scrolling',
      icon: '📜'
    }
  };

  const handleAnimationApply = () => {
    if (!selectedAnimation || !selectedTrigger) return;

    const newEvent = {
      id: Date.now(),
      type: 'animation',
      action: 'animate',
      trigger: selectedTrigger,
      value: {
        animation: selectedAnimation,
        duration: animationDuration,
        delay: animationDelay,
        iterations: animationIterations
      },
      label: `${animationTriggers[selectedTrigger].name}: ${predefinedAnimations[selectedAnimation].name}`
    };

    const updatedEvents = [...appliedEvents, newEvent];
    setAppliedEvents(updatedEvents);
    updateElementInteractions(selectedElement.id, 'events', updatedEvents);

    // Reset animation form and close the dropdown
    setSelectedAnimation('');
    setSelectedTrigger('onView');
    setAnimationDuration('1s');
    setAnimationDelay('0s');
    setAnimationIterations('1');
    setOpenSection(null); // This will close the animations dropdown
  };

  const previewAnimation = () => {
    setIsPreviewingAnimation(true);
    setTimeout(() => setIsPreviewingAnimation(false), 
      parseFloat(animationDuration) * 1000);
  };

  const renderAnimationsSection = () => (
    <div className="event-section">
      <div 
        className={`event-section-header ${openSection === 'animations' ? 'active' : ''}`}
        onClick={() => toggleSection('animations')}
      >
        <div className="event-section-title">
          <span className="event-section-icon">✨</span>
          <span>Animations</span>
        </div>
        <span className={`section-arrow ${openSection === 'animations' ? 'open' : ''}`}>▼</span>
      </div>
      {openSection === 'animations' && (
        <div className="event-section-content">
          <div className="property-group">
            <label>Trigger</label>
            <select 
              className="interaction-select"
              value={selectedTrigger}
              onChange={(e) => setSelectedTrigger(e.target.value)}
            >
              <option value="">Select trigger</option>
              {Object.entries(animationTriggers).map(([key, value]) => (
                <option key={key} value={key}>{value.icon} {value.name}</option>
              ))}
            </select>
          </div>

          <div className="property-group">
            <label>Animation Type</label>
            <select 
              className="interaction-select"
              value={selectedAnimation}
              onChange={(e) => setSelectedAnimation(e.target.value)}
            >
              <option value="">Select animation</option>
              {Object.entries(predefinedAnimations).map(([key, value]) => (
                <option key={key} value={key}>{value.name}</option>
              ))}
            </select>
          </div>

          {selectedAnimation && (
            <>
              <div className="property-group">
                <label>Duration</label>
                <input 
                  type="text"
                  value={animationDuration}
                  onChange={(e) => setAnimationDuration(e.target.value)}
                  placeholder="1s"
                />
              </div>

              <div className="property-group">
                <label>Delay</label>
                <input 
                  type="text"
                  value={animationDelay}
                  onChange={(e) => setAnimationDelay(e.target.value)}
                  placeholder="0s"
                />
              </div>

              <div className="property-group">
                <label>Iterations</label>
                <input 
                  type="text"
                  value={animationIterations}
                  onChange={(e) => setAnimationIterations(e.target.value)}
                  placeholder="1"
                />
              </div>

              <button 
                className="apply-animation-btn"
                onClick={handleAnimationApply}
                disabled={!selectedAnimation || !selectedTrigger}
              >
                Apply Animation
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );

  const handlePreviewAnimation = (event) => {
    if (event.type !== 'animation') return;
    
    // Find the element in the DOM
    const elementNode = document.querySelector(`[data-builder-id="${selectedElement.builderId}"]`);
    if (!elementNode) return;

    // Remove any existing animation
    elementNode.style.animation = 'none';
    
    // Force a reflow by accessing offsetHeight and storing it (even though we don't use the value)
    const reflow = elementNode.offsetHeight;

    // Apply the animation
    const animationProps = `${event.value.animation} ${event.value.duration} ${event.value.delay} ${event.value.iterations === 'infinite' ? 'infinite' : event.value.iterations}`;
    elementNode.style.animation = animationProps;
    elementNode.style.animationFillMode = 'both';

    // Set the previewing state
    setPreviewingEventId(event.id);

    // Reset the previewing state after animation completes
    const duration = parseFloat(event.value.duration) * 1000;
    const delay = parseFloat(event.value.delay) * 1000;
    setTimeout(() => {
      setPreviewingEventId(null);
    }, duration + delay);
  };

  // Render hover events section
  const renderHoverEventsSection = () => (
    <div className="event-section">
      <div 
        className={`event-section-header ${openSection === 'hover' ? 'active' : ''}`}
        onClick={() => toggleSection('hover')}
      >
        <div className="event-section-title">
          <span className="event-section-icon">✋</span>
          <span>Hover Events</span>
        </div>
        <span className={`section-arrow ${openSection === 'hover' ? 'open' : ''}`}>▼</span>
      </div>
      {openSection === 'hover' && (
        <div className="event-section-content">
          <div className="property-group">
            <label>Hover Direction</label>
            <div className="hover-direction-toggle">
              <button 
                className={`hover-direction-btn ${hoverDirection === 'in' ? 'active' : ''}`}
                onClick={() => setHoverDirection('in')}
              >
                Hover In
              </button>
              <button 
                className={`hover-direction-btn ${hoverDirection === 'out' ? 'active' : ''}`}
                onClick={() => setHoverDirection('out')}
              >
                Hover Out
              </button>
            </div>
          </div>
          
          <div className="property-group">
            <label>Effect Type</label>
            <select 
              className="interaction-select"
              value={selectedHoverAction}
              onChange={(e) => handleHoverActionChange(e.target.value)}
            >
              <option value="">Select effect</option>
              <option value="scale">Scale</option>
              <option value="opacity">Change Opacity</option>
              <option value="color">Change Color</option>
              <option value="toggle">Show/Hide Element</option>
              <option value="toggleClass">Toggle Class</option>
            </select>
          </div>
          
          {selectedHoverAction && renderHoverActionSettings()}
          
          {selectedHoverAction && (
            <button 
              className="apply-event-btn"
              onClick={handleApplyHoverEvent}
            >
              Apply Hover {hoverDirection === 'in' ? 'In' : 'Out'} Effect
            </button>
          )}
        </div>
      )}
    </div>
  );

  const handleTabEventApply = () => {
    if (!tabsParentId || !tabButtonClass || !tabsContentParentId || !tabContentClass) return;

    const newEvent = {
      id: Date.now(),
      type: 'tab',
      action: 'tabclick',
      value: {
        tabsParentId,
        tabButtonClass,
        tabsContentParentId,
        tabContentClass
      },
      label: `Tab Click: ${tabButtonClass} & ${tabContentClass}`
    };

    const updatedEvents = [...appliedEvents, newEvent];
    setAppliedEvents(updatedEvents);
    updateElementInteractions(selectedElement.id, 'events', updatedEvents);

    // Reset form
    setOpenSection(null); // Close the section after applying
  };

  // Render tabs event section
  const renderTabEventsSection = () => (
    <div className="event-section">
      <div 
        className={`event-section-header ${openSection === 'tabs' ? 'active' : ''}`}
        onClick={() => toggleSection('tabs')}
      >
        <div className="event-section-title">
          <span className="event-section-icon">📑</span>
          <span>Tab Click Events</span>
        </div>
        <span className={`section-arrow ${openSection === 'tabs' ? 'open' : ''}`}>▼</span>
      </div>
      {openSection === 'tabs' && (
        <div className="event-section-content">
          <div className="property-group">
            <label>All Tab Buttons Parent</label>
            <select 
              value={tabsParentId}
              onChange={(e) => setTabsParentId(e.target.value)}
            >
              <option value="">Select parent element</option>
              {elements
                .filter(element => element.className)
                .map(element => (
                  <option key={element.id} value={element.id}>
                    {getElementLabel(element)}
                  </option>
                ))
              }
            </select>
          </div>
          
          <div className="property-group">
            <label>Tab Button Active Class</label>
            <input 
              type="text"
              value={tabButtonClass}
              onChange={(e) => setTabButtonClass(e.target.value)}
              placeholder="active"
            />
          </div>
          
          <div className="property-group">
            <label>All Tab Content Parent</label>
            <select 
              value={tabsContentParentId}
              onChange={(e) => setTabsContentParentId(e.target.value)}
            >
              <option value="">Select content parent</option>
              {elements
                .filter(element => element.className)
                .map(element => (
                  <option key={element.id} value={element.id}>
                    {getElementLabel(element)}
                  </option>
                ))
              }
            </select>
          </div>
          
          <div className="property-group">
            <label>Tab Content Active Class</label>
            <input 
              type="text"
              value={tabContentClass}
              onChange={(e) => setTabContentClass(e.target.value)}
              placeholder="active"
            />
          </div>
          
          <button 
            className="apply-event-btn"
            onClick={handleTabEventApply}
            disabled={!tabsParentId || !tabButtonClass || !tabsContentParentId || !tabContentClass}
          >
            Apply Tab Click Event
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="interaction-render">
      {/* Applied Events List with improved header */}
      {appliedEvents.length > 0 && (
        <div className="property-section applied-events-section">
          <div className="section-main-header">
            <div className="section-main-title">
              <span className="section-main-icon">⚡</span>
              <span>Applied Events</span>
            </div>
          </div>
          <div className="applied-events-list">
            {appliedEvents.map(event => (
              <div key={event.id} className="applied-event">
                <div className="event-summary" onClick={() => toggleEventDetails(event.id)}>
                  <div className="event-icon">
                    {event.type === 'animation' ? '✨' : 
                     event.type === 'hover' ? '✋' :
                     event.type === 'tab' ? '📑' :
                     event.type === 'click' ? '🖱️' : '⚡'}
                  </div>
                  <div className="event-content">
                    <div className="event-main-info">
                      <span 
                        className="event-type-badge"
                        data-sequence={
                          event.type === 'animation' ? 'animation' : 
                          event.type === 'hover' ? `hover-${event.direction}` : 
                          event.type === 'tab' ? 'tab' :
                          event.sequence
                        }
                      >
                        {event.type === 'animation' ? '✨ Animation' : 
                         event.type === 'hover' ? (event.direction === 'in' ? '✋ Hover In' : '✋ Hover Out') :
                         event.type === 'tab' ? '📑 Tab Click' :
                         event.sequence === 'first' ? '1st Click' : '2nd Click'}
                      </span>
                      <span className="event-action-text">
                        {event.type === 'animation' 
                          ? predefinedAnimations[event.value.animation]?.name
                          : getEventSummaryLabel(event)}
                      </span>
                    </div>
                  </div>
                  <div className="event-controls">
                    {event.type === 'animation' && (
                      <button 
                        className={`preview-circle-btn ${previewingEventId === event.id ? 'previewing' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreviewAnimation(event);
                        }}
                        disabled={previewingEventId !== null}
                        title="Preview animation"
                      >
                        {previewingEventId === event.id ? '■' : '▶'}
                      </button>
                    )}
                    <button 
                      className="remove-event-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeEvent(event.id);
                      }}
                      title="Remove event"
                    >
                      ×
                    </button>
                  </div>
                </div>
                {expandedEventId === event.id && (
                  <div className="event-expanded">
                    {event.type === 'animation' && (
                      <>
                        <div className="event-summary-info">
                          <div className="trigger-info">
                            <span className="trigger-badge" data-trigger={event.trigger}>
                              {animationTriggers[event.trigger]?.icon} {animationTriggers[event.trigger]?.name}
                            </span>
                          </div>
                          <div className="animation-details">
                            <span>{predefinedAnimations[event.value.animation].name}</span>
                            <span>{event.value.duration}</span>
                            <span>{event.value.iterations}×</span>
                          </div>
                        </div>
                        <button
                          className={`preview-animation-btn ${previewingEventId === event.id ? 'previewing' : ''}`}
                          onClick={() => handlePreviewAnimation(event)}
                          disabled={previewingEventId !== null}
                        >
                          {previewingEventId === event.id ? (
                            <>
                              <span>✨</span>
                              <span>Previewing...</span>
                            </>
                          ) : (
                            <>
                              <span>▶</span>
                              <span>Preview Animation</span>
                            </>
                          )}
                        </button>
                      </>
                    )}
                    
                    {event.type === 'hover' && (
                      <div className="event-summary-info">
                        <div className="hover-details">
                          {event.action === 'scale' && <span>Scale: {event.value.scale}</span>}
                          {event.action === 'opacity' && <span>Opacity: {event.value.opacity}</span>}
                          {event.action === 'color' && (
                            <div className="color-preview">
                              <span>Color:</span>
                              <div 
                                className="color-swatch" 
                                style={{backgroundColor: event.value.color}}
                              ></div>
                            </div>
                          )}
                          {event.action === 'toggle' && <span>Display: {event.value.display}</span>}
                          {event.action === 'toggleClass' && <span>Class: {event.value.className}</span>}
                          <span>Duration: {event.value.transitionDuration}</span>
                        </div>
                      </div>
                    )}
                    
                    {event.type === 'tab' && (
                      <div className="event-summary-info">
                        <div className="tab-details">
                          <span>Tab Buttons Parent: {event.value.tabsParentId}</span>
                          <span>Tab Button Class: {event.value.tabButtonClass}</span>
                          <span>Content Parent: {event.value.tabsContentParentId}</span>
                          <span>Content Class: {event.value.tabContentClass}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Events header */}
      <div className="section-main-header">
        <div className="section-main-title">
          <span className="section-main-icon">➕</span>
          <span>Add Events</span>
        </div>
      </div>

      {/* Events sections */}
      <div className="events-sections">
        {renderClickEventsSection()}
        {renderHoverEventsSection()}
        {renderAnimationsSection()}
        {renderTabEventsSection()}

        {/* Scroll Events Section */}
        <div className="event-section">
          <div 
            className={`event-section-header ${openSection === 'scroll' ? 'active' : ''}`}
            onClick={() => toggleSection('scroll')}
          >
            <div className="event-section-title">
              <span className="event-section-icon">📜</span>
              <span>Scroll Effects</span>
            </div>
            <span className={`section-arrow ${openSection === 'scroll' ? 'open' : ''}`}>▼</span>
          </div>
          {openSection === 'scroll' && (
            <div className="event-section-content">
              <div className="property-group">
                <select className="interaction-select">
                  <option value="">Select action</option>
                  <option value="fade">Fade In</option>
                  <option value="slide">Slide In</option>
                  <option value="parallax">Parallax</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Interaction; 